/**
 * Sort Category Registry
 *
 * Declarative configuration system for table column sorting.
 * Maps each column's keySort value to its primitive data type category,
 * which determines the default tooltip text.
 *
 * Architecture:
 * 1. SORT_CATEGORY_REGISTRY: Maps keySort -> data type category
 * 2. TOOLTIP_OVERRIDES: Maps keySort -> custom tooltip text (for special cases)
 * 3. DEFAULT_TOOLTIPS: Maps data type -> tooltip template function
 * 4. getTooltipForSort(): Main public API (checks overrides, then defaults)
 */

/**
 * Primitive data type categories for sorting
 * Each represents a different type of data being sorted
 */
export enum SortCategory {
  STRING = 'STRING',
  STATUS = 'STATUS',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  NUMBER = 'NUMBER',
  DATE_RANGE = 'DATE_RANGE',
  TASK = 'TASK',
  PLAN_DATE = 'PLAN_DATE'
}

/**
 * Tooltip configuration for a specific sort direction
 */
export interface TooltipConfig {
  asc: string;
  desc: string;
}

/**
 * Maps keySort values to their primitive data type categories
 * This defines the default sorting behavior for each column
 */
export const SORT_CATEGORY_REGISTRY: Record<string, SortCategory> = {
  // STRING (90+ entries) - Alphabetical sorting
  'assignee(s)': SortCategory.STRING,
  investment: SortCategory.STRING,
  entity: SortCategory.STRING,
  'client/advisor': SortCategory.STRING,
  manager: SortCategory.STRING,
  'client / advisor': SortCategory.STRING,
  family: SortCategory.STRING,
  name: SortCategory.STRING,
  'investor entity': SortCategory.STRING,
  entityName: SortCategory.STRING,
  assignee: SortCategory.STRING,
  investor: SortCategory.STRING,
  owner: SortCategory.STRING,
  opportunity: SortCategory.STRING,
  deal: SortCategory.STRING,
  'advisory firms': SortCategory.STRING,
  advisor: SortCategory.STRING,
  strategy: SortCategory.STRING,
  commitment: SortCategory.STRING,
  'sub asset class': SortCategory.STRING,
  'investment type': SortCategory.STRING,
  'entity code': SortCategory.STRING,
  'entity name': SortCategory.STRING,
  series: SortCategory.STRING,
  'series code': SortCategory.STRING,
  assignment: SortCategory.STRING,
  status: SortCategory.STRING, // Note: Has override for special text
  'transaction type': SortCategory.STRING,
  'commitment plan': SortCategory.STRING,
  'update name': SortCategory.STRING,
  'action taken by': SortCategory.STRING,
  tier: SortCategory.STRING,
  email: SortCategory.STRING,
  role: SortCategory.STRING,
  task: SortCategory.STRING,
  'completed by': SortCategory.STRING,
  'first name': SortCategory.STRING,
  'last name': SortCategory.STRING,
  roles: SortCategory.STRING,
  'advisory firm': SortCategory.STRING,
  'primary admin': SortCategory.STRING,
  flag: SortCategory.STRING,
  household: SortCategory.STRING,
  action: SortCategory.STRING,
  user: SortCategory.STRING,
  'email tracking': SortCategory.STRING,
  'transaction status': SortCategory.STRING,
  emailRecipient: SortCategory.STRING,
  recipient: SortCategory.STRING,
  'docs sent': SortCategory.STRING,
  'pacing type': SortCategory.STRING,
  'advisor restrictions': SortCategory.STRING,

  // STATUS (16+ entries) - Status progression sorting
  criteria: SortCategory.STATUS,
  'asset classes': SortCategory.STATUS,
  'allocation status': SortCategory.STATUS,
  'asset class': SortCategory.STATUS, // Note: Has override for special text
  'termination reason': SortCategory.STATUS,
  assetClass: SortCategory.STATUS,
  lastStatus: SortCategory.STATUS,

  // DATE (16+ entries) - Chronological sorting
  'last update': SortCategory.DATE,
  'status date': SortCategory.DATE,
  'next close': SortCategory.DATE,
  'final close': SortCategory.DATE,
  finalClose: SortCategory.DATE,
  nextClose: SortCategory.DATE,
  lastStatusDate: SortCategory.DATE,
  'date added': SortCategory.DATE,
  update: SortCategory.DATE,
  'trade date': SortCategory.DATE,
  'last published update': SortCategory.DATE,
  'new AI suggestions': SortCategory.DATE,
  date: SortCategory.DATE,
  completed: SortCategory.DATE,
  'version date': SortCategory.DATE,
  'last login': SortCategory.DATE,
  'action date': SortCategory.DATE,
  vintage: SortCategory.DATE,

  // BOOLEAN (4 entries) - Yes/No sorting
  custodied: SortCategory.BOOLEAN,
  'ubti blocker': SortCategory.BOOLEAN,
  sri: SortCategory.BOOLEAN,
  decision: SortCategory.BOOLEAN,
  'tax exempt suitable': SortCategory.BOOLEAN,

  // DATE_RANGE (3 entries) - Date range with special wording
  'investor docs due': SortCategory.DATE_RANGE,
  'expected close': SortCategory.DATE_RANGE,
  'close date': SortCategory.DATE_RANGE,

  // TASK (2 entries) - Task ordering
  'next task': SortCategory.TASK,
  'action taken': SortCategory.TASK,

  // PLAN_DATE (1 entry) - Plan date sorting
  'plan start': SortCategory.PLAN_DATE

  // NUMBER - Default fallback (no explicit entries needed)
  // Any keySort not found above will default to numeric sorting
};

/**
 * Override map for columns that need custom tooltip text
 * This allows special cases to be handled outside the normal data type patterns
 *
 * Use this when:
 * - A column needs different wording than its data type default
 * - Special business logic dictates unique sort messaging
 * - A/B testing different tooltip formats
 */
const TOOLTIP_OVERRIDES: Record<string, TooltipConfig> = {
  // 'status' uses alphabetical sorting instead of status progression
  status: {
    asc: 'Sort status • A -> Z',
    desc: 'Sort status • Z -> A'
  },

  // 'asset class' uses alphabetical sorting instead of status progression
  'asset class': {
    asc: 'Sort asset class • A -> Z',
    desc: 'Sort asset class • Z -> A'
  },

  // 'criteria' omits the word "statuses" in the tooltip
  criteria: {
    asc: 'Sort criteria • earlier -> later',
    desc: 'Sort criteria • later -> earlier'
  }
};

/**
 * Default tooltip templates for each primitive data type
 * These are used unless overridden in TOOLTIP_OVERRIDES
 */
const DEFAULT_TOOLTIPS: Record<SortCategory, (keySort: string) => TooltipConfig> = {
  [SortCategory.STRING]: (keySort) => ({
    asc: `Sort ${keySort} • A -> Z`,
    desc: `Sort ${keySort} • Z -> A `
  }),

  [SortCategory.STATUS]: (keySort) => ({
    asc: `Sort ${keySort} • earlier statuses -> later statuses`,
    desc: `Sort ${keySort} • later statuses -> earlier statuses`
  }),

  [SortCategory.DATE]: (keySort) => ({
    asc: `Sort ${keySort} • oldest -> newest`,
    desc: `Sort ${keySort} • newest -> oldest`
  }),

  [SortCategory.BOOLEAN]: (keySort) => ({
    asc: `Sort ${keySort}  • No -> Yes`,
    desc: `Sort ${keySort} • Yes -> No`
  }),

  [SortCategory.NUMBER]: (keySort) => ({
    asc: `Sort ${keySort} • Min -> Max`,
    desc: `Sort ${keySort} • Max -> Min`
  }),

  [SortCategory.DATE_RANGE]: (keySort) => ({
    asc: `Sort ${keySort} • Sooner -> Later`,
    desc: `Sort ${keySort} • Later -> Sooner`
  }),

  [SortCategory.TASK]: (keySort) => ({
    asc: `Sort ${keySort} • First -> Last`,
    desc: `Sort ${keySort} • Last -> First`
  }),

  [SortCategory.PLAN_DATE]: (keySort) => ({
    asc: `Sort ${keySort} • Oldest -> Most Recent`,
    desc: `Sort ${keySort} • Most Recent -> Oldest`
  })
};

/**
 * Get the sort category for a given keySort value
 * @param keySort - The column's keySort value
 * @returns The sort category, defaults to NUMBER if not found
 */
export const getSortCategory = (keySort: string): SortCategory => {
  return SORT_CATEGORY_REGISTRY[keySort] ?? SortCategory.NUMBER;
};

/**
 * Get the tooltip configuration for a given keySort
 * Checks overrides first, then falls back to default templates
 * @param keySort - The column's keySort value
 * @returns Tooltip config with asc and desc messages
 */
const getTooltipConfig = (keySort: string): TooltipConfig => {
  // Check if there's a special case override
  if (TOOLTIP_OVERRIDES[keySort]) {
    return TOOLTIP_OVERRIDES[keySort];
  }

  // Use default template for the data type
  const category = getSortCategory(keySort);
  return DEFAULT_TOOLTIPS[category](keySort);
};

/**
 * Get the tooltip title for a sort operation
 * This is the main function that replaces the 96-line getToolTipTitle
 * @param keySort - The column's keySort value
 * @param asc - Whether sorting ascending
 * @returns The formatted tooltip text
 */
export const getTooltipForSort = (keySort: string, asc: boolean): string => {
  const config = getTooltipConfig(keySort);
  return asc ? config.asc : config.desc;
};
